import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "../../../api/axios";
import ModernProductSelectorDialog from "../../utils/Selector/ModernProductSelectorDialog";

export const listButton = "inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed";
export const listPrimary = `${listButton} !border-[#1463C7] !bg-[#1463C7] !text-white hover:!bg-[#0B3A78]`;
export const listInput = "mt-1 block min-h-[44px] w-full rounded-xl border-slate-200 text-sm focus:border-[#1463C7] focus:ring-[#1463C7]";

function ProductCamera({ onScan }) {
  const { t } = useTranslation();
  const elementId = `item-list-camera-${useId().replace(/:/g, "")}`;
  const [failed, setFailed] = useState(false);
  const callback = useRef(onScan);
  callback.current = onScan;
  useEffect(() => {
    let active = true;
    let scanner;
    let completed = false;
    const stop = async () => { if (scanner?.isScanning) await scanner.stop(); scanner?.clear(); };
    import("html5-qrcode").then(async ({ Html5Qrcode }) => {
      if (!active) return;
      scanner = new Html5Qrcode(elementId);
      try {
        await scanner.start({ facingMode: "environment" }, { fps: 10, qrbox: 220 }, (value) => {
          if (active && !completed) { completed = true; callback.current(value); }
        }, () => {});
        if (!active) await stop();
      } catch { if (active) setFailed(true); }
    }).catch(() => { if (active) setFailed(true); });
    return () => { active = false; stop().catch(() => {}); };
  }, [elementId]);
  return <div>{failed && <p role="alert" className="text-sm text-red-700">{t("itemListDetail.camera_failed")}</p>}<div id={elementId} className="overflow-hidden rounded-xl" /></div>;
}

export function ItemListProductPicker({ onSelect, onSelectMany, disabled, multiple = false, autoOpen = false }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(autoOpen);
  const [draft, setDraft] = useState([]);
  const [camera, setCamera] = useState(false);
  const [scanState, setScanState] = useState({});
  const request = useRef(0);
  useEffect(() => () => { request.current += 1; }, []);
  const scan = async (value) => {
    setCamera(false);
    if (disabled) return;
    const seq = ++request.current;
    setScanState({ loading: true });
    try {
      const id = value.match(/\/dashboard\/products\/(\d+)(?:[/?#]|$)/)?.[1];
      const response = await axios.get("/products", { params: {
        ...(id ? { ids: id } : { query: value }), page: 1, per_page: 20,
        fields: "id,title,displayTitle,item_number,purchase_price,sales_price,description,unit_id",
      } });
      if (seq !== request.current) return;
      const rows = response.data?.data;
      const product = Array.isArray(rows) && rows.find((row) => id ? String(row.id) === id : String(row.item_number).toLowerCase() === value.trim().toLowerCase());
      setScanState(product ? { product } : { missing: true });
    } catch { if (seq === request.current) setScanState({ error: true }); }
  };
  const openPicker = () => { setDraft([]); setOpen(true); };
  return <section aria-label={t("itemListDetail.find_product")} className="space-y-3 rounded-2xl bg-slate-50 p-4">
    <div className="flex flex-wrap gap-2">
      <button type="button" className={listPrimary} disabled={disabled} onClick={openPicker}>{t("project.item_list.manual_search")}</button>
      <button type="button" className={listButton} disabled={disabled} onClick={() => setCamera(!camera)}>{t(camera ? "itemListDetail.stop_camera" : "itemListDetail.scan")}</button>
    </div>
    {camera && <ProductCamera onScan={scan} />}
    {scanState.loading && <p role="status">{t("itemListDetail.searching")}</p>}
    {(scanState.error || scanState.missing) && <p role="alert" className="text-sm text-red-700">{t(scanState.error ? "itemListDetail.search_failed" : "itemListDetail.scan_no_exact_match")}</p>}
    {scanState.product && <button type="button" className={listButton} disabled={disabled} onClick={() => { onSelect(scanState.product); setScanState({}); }}>{scanState.product.title} · {t("itemListDetail.select")}</button>}
    {open && <ModernProductSelectorDialog open multiple={multiple} selectedProducts={draft} onClose={() => setOpen(false)}
      onChange={(products) => {
        if (disabled) return;
        setDraft(products);
        if (!multiple && products[0]?.id) { onSelect(products[0]); setOpen(false); }
      }}
      onConfirmSelection={multiple ? () => { if (!disabled && draft.length) { onSelectMany(draft); setOpen(false); } } : undefined}
      confirmSelectionLabel={t("itemListDetail.add_selected", { count: draft.length })}
    />}
  </section>;
}
